import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@/components/ui";
import { LoadingSpinner } from "@/components/common";
import { notify } from "@/lib/notify";
import { useCreateCustomer, useCustomerLookup } from "@/features/customers/api";
import type { Customer } from "@/types";

interface CustomerLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectCustomer: (customer: Customer) => void;
}

export function CustomerLinkDialog({
  open,
  onOpenChange,
  onSelectCustomer,
}: CustomerLinkDialogProps) {
  const [phone, setPhone] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState("");

  const digits = phone.replace(/\D/g, "");
  const { data: customer, isLoading, isError } = useCustomerLookup(digits);
  const createCustomer = useCreateCustomer();

  const handleReset = () => {
    setPhone("");
    setIsCreating(false);
    setNewCustomerName("");
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      handleReset();
    }

    onOpenChange(next);
  };

  const handleLink = () => {
    if (!customer) {
      return;
    }

    onSelectCustomer(customer);
    notify.success(`Linked ${customer.name}`);
    handleReset();
  };

  const handleCreateCustomer = async () => {
    try {
      const newCustomer = await createCustomer.mutateAsync({
        name: newCustomerName.trim(),
        phone: phone.trim(),
      });
      onSelectCustomer(newCustomer);
      notify.success("Customer created and linked");
      handleReset();
    } catch (err) {
      notify.fromError(err, "Failed to create customer");
    }
  };

  const searchReady = digits.length >= 4;
  const notFound = searchReady && isError && !customer;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Link Customer</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="customer-phone">Phone Number</Label>
            <Input
              id="customer-phone"
              placeholder="Enter phone number"
              inputMode="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setIsCreating(false);
              }}
            />
            <p className="text-xs text-muted-foreground">
              Enter at least 4 digits to look up a customer.
            </p>
          </div>
          {isLoading && searchReady && (
            <div className="flex justify-center py-4">
              <LoadingSpinner />
            </div>
          )}

          {customer && !isCreating && (
            <div className="space-y-2">
              <Card>
                <CardContent className="flex items-center justify-between p-4">
                  <div>
                    <p className="font-medium">{customer.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {customer.phone}
                    </p>
                  </div>
                  <Badge>{customer.loyaltyPoints} pts</Badge>
                </CardContent>
              </Card>
              <Button className="w-full" onClick={handleLink}>
                Link this customer
              </Button>
            </div>
          )}

          {notFound && !isCreating && (
            <div className="space-y-2 rounded-md border border-dashed bg-muted/40 p-4 text-center">
              <p className="text-sm text-muted-foreground">
                No customer found for that number.
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setIsCreating(true)}
              >
                Create new customer
              </Button>
            </div>
          )}

          {isCreating && (
            <div className="space-y-3 rounded-md border bg-card p-4">
              <div className="space-y-2">
                <Label htmlFor="customer-name">Customer Name</Label>
                <Input
                  id="customer-name"
                  placeholder="Enter customer name"
                  value={newCustomerName}
                  onChange={(e) => setNewCustomerName(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setIsCreating(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleCreateCustomer}
                  disabled={!newCustomerName.trim() || createCustomer.isPending}
                >
                  {createCustomer.isPending ? "Creating..." : "Create & Link"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
